// src/utils/profile/profileInstantCache.js
// English:
// Session-scoped instant profile snapshots for fast mobile re-entry.
// This is not the source of truth.
// It only paints the last known profile UI immediately while fresh data loads.

const PROFILE_INSTANT_TTL_MS = 5 * 60 * 1000;

export const MY_PROFILE_INSTANT_KEY = "profile:instant:me";

export function buildMyProfileInstantKey(user = null) {
  const id = user?.id || user?.user_id || user?.userId;
  if (id) return `profile:instant:me:id:${id}`;

  const username = String(user?.username || "").trim().toLowerCase();
  if (username) return `profile:instant:me:username:${username}`;

  return MY_PROFILE_INSTANT_KEY;
}

export function buildPublicProfileInstantKey(username = "") {
  const clean = String(username || "").trim().toLowerCase();
  return clean ? `profile:instant:public:${clean}` : "";
}

export function buildLegacyPublicProfileInstantKey(userId = "") {
  const clean = String(userId || "").trim();
  return clean ? `profile:instant:legacy:${clean}` : "";
}

function canUseStorage() {
  return typeof window !== "undefined" && !!window.sessionStorage;
}

export function readProfileInstantSnapshot(cacheKey = "") {
  if (!cacheKey || !canUseStorage()) return null;

  try {
    const raw = window.sessionStorage.getItem(cacheKey);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    const savedAt = Number(parsed?.savedAt || 0);
    const payload = parsed?.payload || null;

    if (!savedAt || !payload) {
      window.sessionStorage.removeItem(cacheKey);
      return null;
    }

    if (Date.now() - savedAt > PROFILE_INSTANT_TTL_MS) {
      window.sessionStorage.removeItem(cacheKey);
      return null;
    }

    return payload;
  } catch {
    return null;
  }
}

export function writeProfileInstantSnapshot(cacheKey = "", payload = null) {
  if (!cacheKey || !payload?.user || !canUseStorage()) return;

  try {
    window.sessionStorage.setItem(
      cacheKey,
      JSON.stringify({
        savedAt: Date.now(),
        payload,
      })
    );
  } catch {
    // English: Ignore storage write failures silently.
  }
}

export function clearProfileInstantSnapshot(cacheKey = "") {
  if (!cacheKey || !canUseStorage()) return;

  try {
    window.sessionStorage.removeItem(cacheKey);
  } catch {
    // English: Ignore storage cleanup failures silently.
  }
}